import React, { useEffect, useMemo, useState } from 'react';
import { Receipt, Loader2, Download, Check, ChevronDown, ChevronRight, Building2, FileText } from 'lucide-react';
import { formatMoneyFromMinor } from '@rehub/pricing';
import {
  getInvoiceClients,
  getClientInvoiceStatement,
  markOrdersInvoiced,
  type InvoiceClient,
  type InvoiceStatementOrder,
} from '../../services/supabase';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function csvEscape(value: string): string {
  if (/[",\n]/.test(value)) return `"${value.replace(/"/g, '""')}"`;
  return value;
}

/** Superadmin "Invoicing" tab: monthly-billed clients, statements + CSV export. */
const InvoicingTab: React.FC = () => {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1); // 1-12

  const [clients, setClients] = useState<InvoiceClient[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [statement, setStatement] = useState<InvoiceStatementOrder[]>([]);
  const [statementLoading, setStatementLoading] = useState(false);
  const [marking, setMarking] = useState(false);

  const loadClients = () => {
    setLoading(true);
    getInvoiceClients().then((rows) => {
      setClients(rows);
      setLoading(false);
    });
  };

  useEffect(() => {
    loadClients();
  }, []);

  const openStatement = async (client: InvoiceClient) => {
    if (expanded === client.user_id) {
      setExpanded(null);
      setStatement([]);
      return;
    }
    setExpanded(client.user_id);
    setStatementLoading(true);
    const rows = await getClientInvoiceStatement(client.user_id, year, month);
    setStatement(rows);
    setStatementLoading(false);
  };

  // Reload the open statement when the month/year changes.
  useEffect(() => {
    if (!expanded) return;
    setStatementLoading(true);
    getClientInvoiceStatement(expanded, year, month).then((rows) => {
      setStatement(rows);
      setStatementLoading(false);
    });
  }, [year, month]); // eslint-disable-line react-hooks/exhaustive-deps

  const statementTotalPence = useMemo(
    () => statement.reduce((sum, o) => sum + (o.invoice_amount_pence || 0), 0),
    [statement]
  );

  const exportCsv = (client: InvoiceClient) => {
    const header = ['Job number', 'Date', 'Property address', 'Sq ft', 'Status', 'Amount (£)', 'Invoiced'];
    const rows = statement.map((o) => [
      o.job_number || '',
      new Date(o.created_at).toLocaleDateString('en-GB'),
      o.property_address || '',
      o.estimated_sq_ft != null ? String(o.estimated_sq_ft) : '',
      o.status,
      ((o.invoice_amount_pence || 0) / 100).toFixed(2),
      o.invoiced_at ? 'Yes' : 'No',
    ]);
    rows.push(['', '', '', '', 'TOTAL', (statementTotalPence / 100).toFixed(2), '']);
    const csv = [header, ...rows].map((r) => r.map((c) => csvEscape(String(c))).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const label = (client.invoice_company || client.email || 'client').replace(/[^a-z0-9]+/gi, '-').toLowerCase();
    a.href = url;
    a.download = `sketchplan-invoice-${label}-${year}-${String(month).padStart(2, '0')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const markInvoiced = async () => {
    const ids = statement.filter((o) => !o.invoiced_at).map((o) => o.id);
    if (ids.length === 0) return;
    setMarking(true);
    const res = await markOrdersInvoiced(ids);
    setMarking(false);
    if (res.success && expanded) {
      const rows = await getClientInvoiceStatement(expanded, year, month);
      setStatement(rows);
      loadClients();
    }
  };

  const years = [now.getFullYear(), now.getFullYear() - 1];

  return (
    <div className="space-y-5">
      {/* Header + month selector */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-2">
          <Receipt size={20} className="text-teal-400" />
          <div>
            <h2 className="text-lg font-semibold text-white">Invoicing</h2>
            <p className="text-sm text-slate-500">Monthly-billed clients and their statements.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={month}
            onChange={(e) => setMonth(Number(e.target.value))}
            className="rounded-lg bg-slate-800 border border-slate-700 text-white text-sm px-3 py-2 focus:outline-none focus:border-teal-500/50"
          >
            {MONTHS.map((m, i) => (
              <option key={m} value={i + 1}>{m}</option>
            ))}
          </select>
          <select
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            className="rounded-lg bg-slate-800 border border-slate-700 text-white text-sm px-3 py-2 focus:outline-none focus:border-teal-500/50"
          >
            {years.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 size={28} className="animate-spin text-teal-400" />
        </div>
      ) : clients.length === 0 ? (
        <div className="text-center py-16 border border-slate-800 rounded-xl bg-slate-900/50">
          <div className="w-12 h-12 bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-4 text-slate-500">
            <Receipt size={22} />
          </div>
          <h3 className="text-lg font-medium text-slate-300">No monthly-invoice clients yet</h3>
          <p className="text-sm text-slate-500 max-w-md mx-auto mt-2">
            Open a user in the Users &amp; Status tab and set their billing to “Monthly invoice” to add them here.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {clients.map((client) => {
            const isOpen = expanded === client.user_id;
            return (
              <div key={client.user_id} className="border border-slate-800 rounded-xl overflow-hidden bg-slate-900/50">
                {/* Client row */}
                <button
                  onClick={() => openStatement(client)}
                  className="w-full flex items-center gap-3 px-4 py-3.5 hover:bg-slate-800/40 transition-colors text-left"
                >
                  {isOpen ? <ChevronDown size={16} className="text-slate-500 shrink-0" /> : <ChevronRight size={16} className="text-slate-500 shrink-0" />}
                  <div className="w-9 h-9 rounded-lg bg-teal-500/15 flex items-center justify-center text-teal-400 shrink-0">
                    <Building2 size={16} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-white font-medium truncate">{client.invoice_company || client.email}</p>
                    <p className="text-xs text-slate-500 truncate">
                      {client.invoice_company ? client.email : 'No company name set'}
                      {client.custom_plan_rate_pence != null && (
                        <span className="ml-2 text-teal-400">· {formatMoneyFromMinor(client.custom_plan_rate_pence, 'GBP')}/plan rate</span>
                      )}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-sm font-semibold text-white">{formatMoneyFromMinor(client.uninvoiced_amount_pence, 'GBP')}</p>
                    <p className="text-[11px] text-slate-500">{client.uninvoiced_count} order{client.uninvoiced_count === 1 ? '' : 's'} to invoice</p>
                  </div>
                </button>

                {/* Statement */}
                {isOpen && (
                  <div className="border-t border-slate-800 px-4 py-4">
                    {statementLoading ? (
                      <div className="flex items-center gap-2 text-slate-500 text-sm py-4">
                        <Loader2 size={14} className="animate-spin" /> Loading statement…
                      </div>
                    ) : statement.length === 0 ? (
                      <p className="text-sm text-slate-500 py-3">No invoice orders for {MONTHS[month - 1]} {year}.</p>
                    ) : (
                      <>
                        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                          <p className="text-xs text-slate-400">
                            {statement.length} order{statement.length === 1 ? '' : 's'} · {MONTHS[month - 1]} {year}
                          </p>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => exportCsv(client)}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-200 text-xs font-medium transition-colors"
                            >
                              <Download size={13} /> Export CSV
                            </button>
                            <button
                              onClick={markInvoiced}
                              disabled={marking || statement.every((o) => o.invoiced_at)}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-teal-600 hover:bg-teal-500 disabled:opacity-50 text-white text-xs font-medium transition-colors"
                            >
                              {marking ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
                              Mark invoiced
                            </button>
                          </div>
                        </div>

                        <div className="overflow-x-auto rounded-lg border border-slate-800">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="bg-slate-800/60 text-left text-[11px] uppercase tracking-wide text-slate-500">
                                <th className="px-3 py-2 font-semibold">Job</th>
                                <th className="px-3 py-2 font-semibold">Date</th>
                                <th className="px-3 py-2 font-semibold">Property</th>
                                <th className="px-3 py-2 font-semibold text-right">Amount</th>
                                <th className="px-3 py-2 font-semibold text-right">Invoiced</th>
                              </tr>
                            </thead>
                            <tbody>
                              {statement.map((o) => (
                                <tr key={o.id} className="border-t border-slate-800/70">
                                  <td className="px-3 py-2 text-slate-400">{o.job_number || '—'}</td>
                                  <td className="px-3 py-2 text-slate-400 whitespace-nowrap">
                                    {new Date(o.created_at).toLocaleDateString('en-GB')}
                                  </td>
                                  <td className="px-3 py-2 text-slate-300 max-w-[200px] truncate">{o.property_address}</td>
                                  <td className="px-3 py-2 text-right text-white font-medium">
                                    {formatMoneyFromMinor(o.invoice_amount_pence || 0, 'GBP')}
                                  </td>
                                  <td className="px-3 py-2 text-right">
                                    {o.invoiced_at ? (
                                      <span className="text-teal-400 inline-flex items-center gap-1 text-xs"><Check size={12} /> Yes</span>
                                    ) : (
                                      <span className="text-slate-500 text-xs">No</span>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                            <tfoot>
                              <tr className="border-t border-slate-700 bg-slate-800/40">
                                <td className="px-3 py-2.5 text-slate-300 font-semibold" colSpan={3}>
                                  <span className="inline-flex items-center gap-1.5"><FileText size={13} /> Total for {MONTHS[month - 1]}</span>
                                </td>
                                <td className="px-3 py-2.5 text-right text-teal-400 font-bold">{formatMoneyFromMinor(statementTotalPence, 'GBP')}</td>
                                <td />
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default InvoicingTab;
